/**
 * PB-2941 — the value/instant conversions the observation surfaces share.
 *
 * Kept out of the components because the update form and the chart must agree
 * on exactly one reading of "is this a value the server will accept" and one
 * reading of "what instant does this local input mean". Two copies of either
 * would drift, and the failure would be a rejected save or a reading filed at
 * the wrong hour — both silent until someone reads the series.
 */

/**
 * Mirrors the server's `DECIMAL(18,4)` guard (`IsRequiredDecimalValue`) exactly:
 * up to 14 integer digits, up to 4 decimal places, optional leading sign,
 * anchored so surrounding whitespace fails rather than being trimmed away.
 *
 * Client-side it is a courtesy — it turns a typo into an inline message instead
 * of a round-trip — and the server rejects the same shapes regardless.
 */
const DECIMAL_18_4 = /^-?\d{1,14}(\.\d{1,4})?$/;

export function isRecordableKpiValue(value: string): boolean {
  return DECIMAL_18_4.test(value);
}

/** The wording the TTI page's monthly Actual box uses for the same rule, so a
 *  figure is refused in the same words wherever it is typed (KS-08, PB-5464). */
export const KPI_NUMBER_MESSAGE =
  "Enter a number — digits only, up to 4 decimal places.";

/**
 * A target's baseline / target value must be a number: plan vs actual, "Fill
 * target linearly" and the red/green verdict are all computed from it, and
 * "abc" saved here hid a TTI's whole monthly table behind "Not quantified yet"
 * (KS-08). `stored` is the value already saved — a target written before this
 * check ("96 %") may keep it unchanged, but any NEW value is judged.
 */
export function kpiTargetFigureError(
  value: string,
  stored?: string | null,
): string | undefined {
  const typed = value.trim();
  if (typed === "" || typed === (stored ?? "").trim()) return undefined;
  return isRecordableKpiValue(typed) ? undefined : KPI_NUMBER_MESSAGE;
}

/** Plain decimal notation, which is all `Decimal.toString()` can emit for a
 * `DECIMAL(18,4)`: its exponential thresholds (±1e21 / 1e-7) sit far outside the
 * column's range, so a wire value is always digits with an optional sign and
 * fraction. Deliberately looser than {@link isRecordableKpiValue} — that one
 * guards a WRITE against the column's precision, this one only has to recognise
 * a number the server already stored. */
const PLAIN_DECIMAL = /^-?\d+(\.\d+)?$/;

/**
 * Parse a wire decimal string for CHARTING only.
 *
 * `Number()` is NOT used as the test, because it accepts things that are not
 * numbers on this wire and converts them silently: `Number("")` and
 * `Number("  ")` are **0**, `Number("0x1f")` is 31. An empty value plotted as
 * zero is the worst of those — a chart would show a real reading of nought where
 * the truth is "no value", and nothing on screen would say otherwise. So the
 * shape is checked first and anything else is `null`.
 *
 * `null` rather than `NaN` for the same reason: a caller can branch on null, and
 * plotting NaN silently punches a hole in the line.
 *
 * Nothing the user READS may come from this. A `DECIMAL(18,4)` beyond
 * `Number.MAX_SAFE_INTEGER` loses precision here, which is invisible at chart
 * resolution and wrong in a readout — the readouts render the original string.
 */
export function parseObservationValue(value: string): number | null {
  if (!PLAIN_DECIMAL.test(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * `datetime-local` speaks LOCAL wall-clock time with no zone, so both directions
 * are conversions rather than formatting.
 *
 * `nowAsLocalInput` renders the current instant in the user's zone, which is
 * what makes the default read as "now" on their clock rather than in UTC.
 */
export function nowAsLocalInput(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}`
  );
}

/** Date portion consumed by the design-system DatePicker. */
export function localInputDate(value: string): string {
  return value.slice(0, 10);
}

/**
 * Replace only the calendar date while retaining the already-seeded local time.
 * KPI observations remain instants on the wire, but the user does not need the
 * browser-native datetime control just to backdate a periodic reading.
 *
 * CLEARING the date clears the whole value. The DatePicker reports a cleared
 * field as `""`, and this used to splice that into `` `T${time}` `` — a string
 * that is TRUTHY and unparseable, so every "is a date present?" check passed
 * while every parse failed. In the single-reading form that combination sent no
 * `observedAt` at all and the server filed the reading at ITS now: the user
 * cleared the date to backdate a reading and got today's instead, with nothing
 * on screen saying so. Absence has to be representable as absence.
 */
export function withLocalInputDate(value: string, date: string): string {
  if (!date) return "";
  const time = value.slice(11) || nowAsLocalInput().slice(11);
  return `${date}T${time}`;
}

/** The one shape a `datetime-local` input (or {@link withLocalInputDate}'s
 * splice of one) can carry: date, `T`, minutes, optional seconds. Anything
 * else is not this surface's input and must not be guessed at. */
const LOCAL_INPUT_SHAPE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * `YYYY-MM-DDTHH:mm` (local) → an ISO-8601 instant, or `undefined` when the
 * value is not one.
 *
 * Parsed EXPLICITLY, component by component, then round-tripped — not
 * `new Date(value)` on the whole string. Two reasons, both silent failures:
 * `new Date` accepts shapes that are not datetime-local at all (a bare date
 * parses as UTC midnight — a different instant from the local one the form
 * means), and it NORMALIZES an impossible calendar date instead of failing —
 * `2026-02-31T10:00` came back as March 3rd, so a typo'd reading was filed on
 * a day the user never typed with nothing on screen saying so. The component
 * constructor still does the zone work (a local wall clock in the runtime's
 * zone, which is the zone the user typed in), and the round-trip catches
 * every normalization — including a wall time the zone skipped over a DST
 * spring-forward, which never occurred and is refused rather than shifted.
 *
 * `undefined` means "this is not an instant", and every caller must treat it as
 * a REASON TO REFUSE rather than as a licence to omit `observedAt`. Omitting it
 * makes the server record at its own `now`, which is a different reading from
 * the one the user was describing — see {@link withLocalInputDate} for the
 * cleared-field case that used to take exactly that path.
 */
export function localInputToIso(value: string): string | undefined {
  const match = LOCAL_INPUT_SHAPE.exec(value);
  if (!match) return undefined;
  const [, year, month, day, hours, minutes, seconds] = match;
  const y = Number(year);
  const mo = Number(month);
  const d = Number(day);
  const h = Number(hours);
  const mi = Number(minutes);
  const s = Number(seconds ?? "0");
  const parsed = new Date(y, mo - 1, d, h, mi, s);
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getFullYear() !== y ||
    parsed.getMonth() !== mo - 1 ||
    parsed.getDate() !== d ||
    parsed.getHours() !== h ||
    parsed.getMinutes() !== mi ||
    parsed.getSeconds() !== s
  ) {
    return undefined;
  }
  return parsed.toISOString();
}
